/**
 * ProductsPage.tsx — the Products page (stories E0-BO-NUM6, redesign layout A).
 *
 * ONE list (`ProductList`): per product, its code/name, collateral and
 * pricing, a catalogue Active/Inactive badge, and an "Available to this
 * tenant" switch. The two facts keep two mechanisms (badge vs. switch) so they
 * never look alike (N3 AC4). A row menu holds Retire.
 *   - Enabling availability is a single-step write. Disabling opens an inline
 *     confirm first (amber bar, reversible); the switch never fires the write.
 *   - Retire (platform-wide, `retireProduct()` — POST .../retire, never a
 *     delete; the UI has no un-retire) opens a red-bar confirm first.
 *   - A globally-inactive product's switch is disabled with an inline reason
 *     and no menu. No delete affordance anywhere in this file.
 * `NewProductForm` is create-only; retiring is never a control on it.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, UnauthorizedError, type ApiClient } from '../../api/client';
import type { ProductConfig, TenantProductAvailability } from '../../api/types';
import { UnauthorizedScreen } from '../../auth/UnauthorizedScreen';
import { NewProductForm } from './NewProductForm';
import { useProductsQuery } from './useProductsQuery';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Notice } from '../../components/Notice';
import { Skeleton } from '../../components/Skeleton';
import floating from '../../lib/floating';

export interface ProductsPageProps {
  apiClient: ApiClient;
}

type ToggleWriteError = { productCode: string; kind: 'notFound' | 'unavailable' | 'unauthorized' };
const classifyToggleError = (error: unknown): 'notFound' | 'unavailable' | 'unauthorized' => {
  if (error instanceof UnauthorizedError) return 'unauthorized';
  if (error instanceof ApiError && error.status === 404) return 'notFound';
  return 'unavailable';
};
const ProductStatusBadge = ({ isActive }: { isActive: boolean }) => {
  const { t } = useTranslation();
  return isActive ? (
    <Badge tone="ok" data-testid="products-status-active">
      {t('products.catalogue.active')}
    </Badge>
  ) : (
    <Badge tone="neutral" hollow data-testid="products-status-inactive">
      {t('products.catalogue.inactive')}
    </Badge>
  );
};
/** Inline confirm: 4px left bar (red = platform-wide retire, amber = reversible per-tenant stop). */
const ConfirmPanel = ({
  testId,
  label,
  tone,
  body,
  confirmLabel,
  confirmClassName,
  cancelLabel,
  submitTestId,
  cancelTestId,
  busy,
  onConfirm,
  onCancel,
}: {
  testId: string;
  label: string;
  tone: 'down' | 'degraded';
  body: string;
  confirmLabel: string;
  confirmClassName: string;
  cancelLabel: string;
  submitTestId: string;
  cancelTestId: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  // Focus moves to the panel on open (WCAG 2.4.3); Esc cancels.
  // Latest props via a ref so the mount-only effect never re-focuses the panel.
  const latest = useRef({ busy, onCancel });
  latest.current = { busy, onCancel };
  useEffect(() => {
    const el = ref.current;
    el?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !latest.current.busy) latest.current.onCancel();
    };
    el?.addEventListener('keydown', onKey);
    return () => el?.removeEventListener('keydown', onKey);
  }, []);
  const btn =
    'inline-flex items-center justify-center rounded-lg px-3 py-1 text-meta font-medium disabled:opacity-60 max-md:min-h-11';
  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="group"
      aria-label={label}
      data-testid={testId}
      className={`col-span-full flex flex-col gap-3 rounded-lg border-l-4 bg-surface-2 p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus ${
        tone === 'down' ? 'border-status-down' : 'border-status-degraded'
      }`}
    >
      <p className="text-body text-ink-1">{body}</p>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          data-testid={submitTestId}
          disabled={busy}
          aria-busy={busy}
          onClick={onConfirm}
          className={`${btn} ${confirmClassName}`}
        >
          {confirmLabel}
        </button>
        <button
          type="button"
          data-testid={cancelTestId}
          disabled={busy}
          onClick={onCancel}
          className={`${btn} bg-surface-2 text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-3`}
        >
          {cancelLabel}
        </button>
      </div>
    </div>
  );
};

/** Row menu (Esc closes and returns focus; outside click closes). Holds Retire only. */
const RowMenu = ({ product, onRetire }: { product: ProductConfig; onRetire: () => void }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    itemRef.current?.focus();
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    wrapRef.current?.addEventListener('keydown', onKey);
    const wrap = wrapRef.current;
    return () => {
      document.removeEventListener('mousedown', onDown);
      wrap?.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative justify-self-end max-md:col-start-2 max-md:row-start-1">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('products.catalogue.rowActions', { product: product.display_name })}
        data-testid={`products-menu-${product.product_code}`}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-ink-1 hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus"
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-10 min-w-[12rem] rounded-lg bg-surface-3 p-1 ring-1 ring-inset ring-control"
        >
          <button
            ref={itemRef}
            type="button"
            role="menuitem"
            data-testid={`products-retire-${product.product_code}`}
            onClick={() => {
              setOpen(false);
              onRetire();
            }}
            className="block min-h-11 w-full rounded-md px-3 py-2 text-left text-body text-status-down hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus"
          >
            {t('products.catalogue.retire')}
          </button>
        </div>
      ) : null}
    </div>
  );
};

const AvailabilitySwitch = ({
  product,
  isAvailable,
  busy,
  onToggle,
}: {
  product: ProductConfig;
  isAvailable: boolean;
  busy: boolean;
  onToggle: () => void;
}) => {
  const { t } = useTranslation();
  const retired = !product.is_active;
  const noteId = `products-retired-note-${product.product_code}`;
  return (
    <>
      <button
        type="button"
        role="switch"
        aria-checked={isAvailable}
        aria-label={t('products.toggleGrid.toggleAria', { product: product.display_name })}
        aria-describedby={retired ? noteId : undefined}
        data-testid={`products-toggle-${product.product_code}`}
        disabled={retired || busy}
        aria-busy={busy}
        onClick={onToggle}
        className="inline-flex min-h-6 items-center gap-2.5 text-body text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus disabled:opacity-60 max-md:min-h-11"
      >
        <span
          aria-hidden="true"
          className={`inline-flex h-6 w-11 flex-none items-center rounded-full transition-colors ${
            isAvailable ? 'bg-status-ok' : 'bg-control'
          }`}
        >
          <span
            className={`h-5 w-5 rounded-full bg-canvas transition-transform ${
              isAvailable ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </span>
        <span aria-hidden="true" className="min-w-[2.2em] font-medium">
          {isAvailable ? t('products.toggleGrid.on') : t('products.toggleGrid.off')}
        </span>
      </button>
      {retired ? (
        <small
          id={noteId}
          data-testid={`products-toggle-note-${product.product_code}`}
          className="block text-meta text-ink-2"
        >
          {t('products.toggleGrid.globallyInactiveNote')}
        </small>
      ) : null}
    </>
  );
};

// --- The single product list (catalogue + tenant availability) ---------------

const ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_2.75rem] gap-x-2 gap-y-1.5 px-4 py-3.5 md:grid-cols-[minmax(0,2.2fr)_minmax(0,1.3fr)_7rem_12rem_2.75rem] md:items-center md:gap-4 md:py-2.5';

const ProductList = ({
  catalogue,
  availabilityByCode,
  rowBusy,
  pendingDisable,
  toggleError,
  retireBusy,
  pendingRetire,
  retireError,
  onEnable,
  onRequestDisable,
  onCancelDisable,
  onConfirmDisable,
  onRequestRetire,
  onCancelRetire,
  onConfirmRetire,
}: {
  catalogue: ProductConfig[];
  availabilityByCode: Map<string, TenantProductAvailability>;
  rowBusy: string | null;
  pendingDisable: ProductConfig | null;
  toggleError: ToggleWriteError | null;
  retireBusy: string | null;
  pendingRetire: ProductConfig | null;
  retireError: ToggleWriteError | null;
  onEnable: (product: ProductConfig) => void;
  onRequestDisable: (product: ProductConfig) => void;
  onCancelDisable: () => void;
  onConfirmDisable: () => void;
  onRequestRetire: (product: ProductConfig) => void;
  onCancelRetire: () => void;
  onConfirmRetire: () => void;
}) => {
  const { t } = useTranslation();
  if (catalogue.length === 0) {
    return (
      <EmptyState data-testid="products-catalogue-empty" title={t('products.catalogue.empty')} />
    );
  }
  const noneOffered = !catalogue.some(
    (p) => availabilityByCode.get(p.product_code)?.is_active === true,
  );
  const label = 'text-meta text-ink-2 md:hidden';
  return (
    <>
      {noneOffered ? (
        <div
          role="status"
          data-testid="products-availability-empty"
          className="rounded-xl bg-surface-2 p-4 text-body text-ink-2"
        >
          <p>{t('products.availability.empty')}</p>
          <p className="text-meta text-ink-3">{t('products.availability.emptyNotAnError')}</p>
        </div>
      ) : null}
      <div
        role="table"
        aria-label={t('products.catalogue.tableLabel')}
        data-testid="products-catalogue-table"
        className="rounded-xl bg-surface-1 ring-1 ring-inset ring-ink-3/10"
      >
        <div
          role="row"
          className="grid-cols-[minmax(0,2.2fr)_minmax(0,1.3fr)_7rem_12rem_2.75rem] gap-4 rounded-t-xl bg-surface-2 px-4 py-2 text-meta font-medium text-ink-2 max-md:hidden md:grid"
        >
          <div role="columnheader">{t('products.catalogue.columns.product')}</div>
          <div role="columnheader">{t('products.catalogue.columns.collateralPricing')}</div>
          <div role="columnheader">{t('products.catalogue.columns.status')}</div>
          <div role="columnheader">{t('products.catalogue.columns.available')}</div>
          <div role="columnheader">
            <span className="sr-only">{t('products.catalogue.columns.actions')}</span>
          </div>
        </div>
        {catalogue.map((product) => {
          const code = product.product_code;
          const isAvailable = availabilityByCode.get(code)?.is_active ?? false;
          const retired = !product.is_active;
          return (
            <div
              key={code}
              role="row"
              data-testid="products-catalogue-row"
              data-product-code={code}
              data-active={retired ? 'false' : 'true'}
              className={`${ROW_GRID} border-t border-outline`}
            >
              <div role="cell" className="min-w-0">
                <div className={`font-semibold ${retired ? 'text-ink-2' : 'text-ink-1'}`}>
                  {product.display_name}
                </div>
                <div className="font-mono text-meta text-ink-2">{code}</div>
              </div>
              <div role="cell" className="text-ink-2 max-md:col-span-2">
                <span className={`${label} mr-1.5`}>
                  {t('products.catalogue.columns.collateralPricing')}
                </span>
                {t(`products.collateralModel.${product.collateral_model}`)} ·{' '}
                {t(`products.pricingModel.${product.pricing_model}`)}
              </div>
              <div role="cell" className="max-md:col-span-2">
                <span className={`${label} mr-1.5`}>{t('products.catalogue.columns.status')}</span>
                <ProductStatusBadge isActive={product.is_active} />
              </div>
              <div
                role="cell"
                className="max-md:col-span-2 max-md:border-t max-md:border-outline max-md:pt-1"
              >
                <span className={`${label} block`}>
                  {t('products.catalogue.columns.available')}
                </span>
                <AvailabilitySwitch
                  product={product}
                  isAvailable={isAvailable}
                  busy={rowBusy === code}
                  onToggle={() => (isAvailable ? onRequestDisable(product) : onEnable(product))}
                />
              </div>
              {retired ? (
                <span aria-hidden="true" />
              ) : (
                <RowMenu product={product} onRetire={() => onRequestRetire(product)} />
              )}
              {toggleError != null && toggleError.productCode === code ? (
                <p
                  role="alert"
                  data-testid={`products-toggle-error-${code}`}
                  className="col-span-full text-meta text-status-down"
                >
                  {t(`products.toggleGrid.errors.${toggleError.kind}`)}
                </p>
              ) : null}
              {retireError != null && retireError.productCode === code ? (
                <p
                  role="alert"
                  data-testid={`products-retire-error-${code}`}
                  className="col-span-full text-meta text-status-down"
                >
                  {t(`products.catalogue.retireErrors.${retireError.kind}`)}
                </p>
              ) : null}
              {pendingDisable?.product_code === code ? (
                <ConfirmPanel
                  testId="products-disable-confirm"
                  label={t('products.toggleGrid.disableConfirmHeading')}
                  tone="degraded"
                  body={t('products.toggleGrid.disableConfirmBody', {
                    product: product.display_name,
                  })}
                  confirmLabel={
                    rowBusy === code
                      ? t('products.toggleGrid.disabling')
                      : t('products.toggleGrid.disableConfirm')
                  }
                  confirmClassName="bg-surface-3 text-ink-1 ring-1 ring-inset ring-control hover:bg-surface-2"
                  cancelLabel={t('products.toggleGrid.disableCancel')}
                  submitTestId="products-disable-confirm-submit"
                  cancelTestId="products-disable-cancel"
                  busy={rowBusy === code}
                  onConfirm={onConfirmDisable}
                  onCancel={onCancelDisable}
                />
              ) : null}
              {pendingRetire?.product_code === code ? (
                <ConfirmPanel
                  testId="products-retire-confirm"
                  label={t('products.catalogue.retireConfirmHeading')}
                  tone="down"
                  body={t('products.catalogue.retireConfirmBody', {
                    product: product.display_name,
                  })}
                  confirmLabel={
                    retireBusy === code
                      ? t('products.catalogue.retiring')
                      : t('products.catalogue.retireConfirm')
                  }
                  confirmClassName="bg-status-down text-canvas hover:bg-status-down/90"
                  cancelLabel={t('products.catalogue.retireCancel')}
                  submitTestId="products-retire-confirm-submit"
                  cancelTestId="products-retire-cancel"
                  busy={retireBusy === code}
                  onConfirm={onConfirmRetire}
                  onCancel={onCancelRetire}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
};

export const ProductsPage = ({ apiClient }: ProductsPageProps) => {
  const { t } = useTranslation();
  const { catalogue, availability, phase, reload } = useProductsQuery(apiClient);

  const [showNewProductForm, setShowNewProductForm] = useState(false);
  const [pendingDisable, setPendingDisable] = useState<ProductConfig | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<ToggleWriteError | null>(null);
  const [pendingRetire, setPendingRetire] = useState<ProductConfig | null>(null);
  const [retireBusy, setRetireBusy] = useState<string | null>(null);
  const [retireError, setRetireError] = useState<ToggleWriteError | null>(null);

  const availabilityByCode = useMemo(
    () => new Map(availability.map((row) => [row.product_code, row])),
    [availability],
  );

  if (phase === 'unauthorized') {
    return <UnauthorizedScreen />;
  }

  const handleEnable = async (product: ProductConfig) => {
    setToggleError(null);
    setRowBusy(product.product_code);
    try {
      await apiClient.enableTenantProduct(product.product_code);
      await reload();
    } catch (error) {
      setToggleError({ productCode: product.product_code, kind: classifyToggleError(error) });
    } finally {
      setRowBusy(null);
    }
  };

  const handleDisableConfirm = async () => {
    if (pendingDisable == null) return;
    const product = pendingDisable;
    setToggleError(null);
    setRowBusy(product.product_code);
    try {
      await apiClient.disableTenantProduct(product.product_code);
      setPendingDisable(null);
      await reload();
    } catch (error) {
      setToggleError({ productCode: product.product_code, kind: classifyToggleError(error) });
      setPendingDisable(null);
    } finally {
      setRowBusy(null);
    }
  };

  const handleRetireConfirm = async () => {
    if (pendingRetire == null) return;
    const product = pendingRetire;
    setRetireError(null);
    setRetireBusy(product.product_code);
    try {
      await apiClient.retireProduct(product.product_code);
      setPendingRetire(null);
      await reload();
    } catch (error) {
      setRetireError({ productCode: product.product_code, kind: classifyToggleError(error) });
      setPendingRetire(null);
    } finally {
      setRetireBusy(null);
    }
  };

  const renderBranch1 = () => {
    if (phase === 'loading')
      return <Skeleton data-testid="products-loading" label={t('products.loading')} />;
    if (phase === 'error')
      return (
        <Notice
          tone="danger"
          data-testid="products-error"
          action={
            <Button data-testid="products-retry" onClick={() => floating(reload())}>
              {t('products.retry')}
            </Button>
          }
        >
          {t('products.errorBody')}
        </Notice>
      );
    return (
      <ProductList
        catalogue={catalogue}
        availabilityByCode={availabilityByCode}
        rowBusy={rowBusy}
        pendingDisable={pendingDisable}
        toggleError={toggleError}
        retireBusy={retireBusy}
        pendingRetire={pendingRetire}
        retireError={retireError}
        onEnable={(product) => floating(handleEnable(product))}
        onRequestDisable={(product) => {
          setToggleError(null);
          setPendingRetire(null);
          setPendingDisable(product);
        }}
        onCancelDisable={() => setPendingDisable(null)}
        onConfirmDisable={() => floating(handleDisableConfirm())}
        onRequestRetire={(product) => {
          setRetireError(null);
          setPendingDisable(null);
          setPendingRetire(product);
        }}
        onCancelRetire={() => setPendingRetire(null)}
        onConfirmRetire={() => floating(handleRetireConfirm())}
      />
    );
  };

  return (
    <section aria-labelledby="products-heading" className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 id="products-heading" className="text-h1 text-ink-1">
          {t('products.title')}
        </h1>
        {!showNewProductForm ? (
          <button
            type="button"
            data-testid="products-new-product-cta"
            onClick={() => setShowNewProductForm(true)}
            className="inline-flex min-h-11 items-center rounded-lg bg-gold px-4 py-1.5 text-body font-medium text-on-gold hover:bg-gold/90"
          >
            {t('products.newProduct.cta')}
          </button>
        ) : null}
      </header>

      {showNewProductForm ? (
        <NewProductForm
          apiClient={apiClient}
          onCancel={() => setShowNewProductForm(false)}
          onCreated={() => {
            setShowNewProductForm(false);
            floating(reload());
          }}
        />
      ) : null}

      {renderBranch1()}
    </section>
  );
};
